import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import AdminThemeGuard from './AdminThemeGuard';
import { VIDEO_PORTFOLIO_ENABLED } from '../config/features';

const SERVICE_LINKS = [
  { to: '/services/social-media', label: 'Social Media Marketing' },
  { to: '/services/branding', label: 'Branding & Identity' },
  { to: '/services/design', label: 'Design & Creative' },
  { to: '/services/web-development', label: 'Web Development' },
  { to: '/services/marketing', label: 'Marketing Strategy' },
];

const WORK_LINKS = [
  { to: '/work', label: 'Case Studies' },
  { to: '/website-portfolio', label: 'Website Portfolio' },
  { to: '/design-portfolio', label: 'Design & Branding' },
  ...(VIDEO_PORTFOLIO_ENABLED ? [{ to: '/portfolio', label: 'Video Portfolio' }] : []),
  { to: '/graphic-portfolio', label: 'Graphic Portfolio' },
];

const WHATSAPP_NUMBER = '919587773274';

/** WhatsApp link with the page the visitor is on prefilled, so the team has context. */
function whatsappHref(path) {
  const where = path === '/' ? 'your website' : `your ${path.replace(/^\//, '').split('/')[0].replace(/-/g, ' ')} page`;
  const text = `Hi Apnaaapan, I found you through ${where} and want to talk about growing my brand.`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
}

const WhatsAppIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
  </svg>
);

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-apn-orange focus-visible:ring-offset-2 focus-visible:ring-offset-apn-bg';

const NAV = [
  { label: 'Services', to: '/services/social-media', match: '/services', children: SERVICE_LINKS },
  { label: 'Work', to: '/work', match: ['/work', '/website-portfolio', '/design-portfolio', '/portfolio', '/graphic-portfolio'], children: WORK_LINKS },
  { label: 'Our Story', to: '/our-story', match: '/our-story' },
  { label: 'Contact', to: '/contact', match: '/contact' },
];

function isActive(item, path) {
  const matches = Array.isArray(item.match) ? item.match : [item.match];
  return matches.some((m) => path === m || path.startsWith(`${m}/`));
}

const Underline = ({ active }) => (
  <span
    className={`absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 to-yellow-400 rounded-full transition-opacity duration-200 ${
      active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
    }`}
  />
);

const DesktopDropdown = ({ item, active }) => {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  const openMenu = () => {
    clearTimeout(closeTimeoutRef.current);
    setOpen(true);
  };
  // Delay prevents the menu from disappearing while moving the pointer into it
  const closeMenu = () => {
    closeTimeoutRef.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
      onFocus={openMenu}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) closeMenu();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <Link
        to={item.to}
        aria-haspopup="true"
        aria-expanded={open}
        className={`font-text font-semibold text-[15px] transition-colors duration-200 relative group inline-flex items-center gap-1 rounded-sm hover:text-apn-ink ${focusRing} ${
          active ? 'text-apn-ink' : 'text-apn-muted'
        }`}
      >
        {item.label}
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
        <Underline active={active} />
      </Link>

      <div
        className={`absolute -left-3 top-[calc(100%+12px)] border border-apn-line rounded-2xl shadow-[0_18px_40px_-12px_rgba(20,22,31,0.25)] dark:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.7)] z-50 min-w-[240px] overflow-hidden bg-apn-surface p-1.5 ${
          open ? 'block' : 'hidden'
        }`}
      >
        {item.children.map((child, i) => (
          <React.Fragment key={child.to}>
            <Link
              to={child.to}
              onClick={() => setOpen(false)}
              className="block rounded-xl px-3.5 py-2.5 text-sm font-medium text-apn-text hover:bg-apn-surface-2 hover:text-apn-orange focus:bg-apn-surface-2 focus:text-apn-orange focus:outline-none transition-colors duration-150"
            >
              {child.label}
            </Link>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const Header = ({ progressBarOffset = false }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const [showHeader, setShowHeader] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const isAdmin = currentPath === '/admin' || currentPath.startsWith('/admin/');
  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY || 0;
      if (!tickingRef.current) {
        window.requestAnimationFrame(() => {
          const lastY = lastScrollYRef.current;
          const isScrollingDown = currentY > lastY;
          const distance = Math.abs(currentY - lastY);

          setScrolled(currentY > 10);
          if (currentY < 10) {
            setShowHeader(true);
          } else if (distance > 4) {
            setShowHeader(!isScrollingDown);
          }

          lastScrollYRef.current = currentY;
          tickingRef.current = false;
        });
        tickingRef.current = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenMobileGroup(null);
  }, [currentPath]);

  return (
    <header
      className={`fixed left-0 right-0 z-50 border-b transition-[transform,background-color,border-color] duration-200 ${progressBarOffset ? 'top-[3px]' : 'top-0'} ${showHeader || isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'} ${
        scrolled || isMobileMenuOpen
          ? 'bg-apn-bg/90 supports-[backdrop-filter]:bg-apn-bg/75 backdrop-blur-md border-apn-line'
          : 'bg-apn-bg border-transparent'
      }`}
    >
      <AdminThemeGuard />
      <nav className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 md:px-8 py-3 md:py-4" aria-label="Main">
        <Link to="/" className={`flex items-center shrink-0 rounded-md ${focusRing}`} aria-label="Apnaaapan home">
          <img
            src="/images/apnaaapan logo.webp"
            alt="Apnaaapan"
            width="183"
            height="40"
            className="h-8 sm:h-10 w-auto select-none hover:opacity-80 transition-opacity duration-200"
            style={{ marginTop: '-2px' }}
          />
        </Link>

        <div className="hidden md:flex items-center space-x-8 relative">
          {NAV.map((item) =>
            item.children ? (
              <DesktopDropdown key={item.label} item={item} active={isActive(item, currentPath)} />
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className={`font-text font-semibold text-[15px] transition-colors duration-200 relative group rounded-sm hover:text-apn-ink ${focusRing} ${
                  isActive(item, currentPath) ? 'text-apn-ink' : 'text-apn-muted'
                }`}
              >
                {item.label}
                <Underline active={isActive(item, currentPath)} />
              </Link>
            )
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Hidden (not unmounted) on admin so its state stays in sync with the saved theme */}
          <ThemeToggle className={isAdmin ? 'hidden' : ''} />
          <a
            href={whatsappHref(currentPath)}
            target="_blank"
            rel="noopener noreferrer"
            data-cta="header-whatsapp"
            aria-label="Chat with us on WhatsApp"
            className={`hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-apn-line bg-apn-surface text-[#1FA855] dark:text-[#25D366] transition-colors hover:border-[#25D366] ${focusRing}`}
          >
            <WhatsAppIcon />
          </a>
          <Link
            to="/book-call"
            data-cta="header-book-call"
            className={`hidden sm:flex bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-3.5 py-2 md:px-5 md:py-2.5 rounded-full font-display font-semibold tracking-tight text-xs md:text-sm items-center gap-2 shadow-sm hover:shadow-[0_8px_24px_-6px_rgba(242,107,42,0.6)] transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 whitespace-nowrap ${focusRing}`}
          >
            <span>Book a Free Call</span>
            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
          <button
            type="button"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            className={`md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-apn-line bg-apn-surface text-apn-ink ${focusRing}`}
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-6 h-6" aria-hidden="true">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 6l12 12M18 6L6 18" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div
        id="mobile-menu"
        className={`md:hidden absolute top-full left-0 right-0 bg-apn-bg border-b border-apn-line shadow-[0_16px_32px_-16px_rgba(0,0,0,0.35)] transition-[max-height] duration-300 overflow-y-auto ${isMobileMenuOpen ? 'max-h-[80vh] visible' : 'max-h-0 invisible'}`}
      >
        <div className="px-4 py-3 space-y-1">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label}>
                <button
                  type="button"
                  aria-expanded={openMobileGroup === item.label}
                  onClick={() => setOpenMobileGroup(openMobileGroup === item.label ? null : item.label)}
                  className={`w-full flex items-center justify-between py-3 font-display text-lg font-semibold tracking-tight rounded-md ${focusRing} ${isActive(item, currentPath) ? 'text-apn-orange' : 'text-apn-ink'}`}
                >
                  {item.label}
                  <svg className={`w-4 h-4 transition-transform ${openMobileGroup === item.label ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openMobileGroup === item.label && (
                  <div className="px-2 py-1 mb-2 bg-apn-surface rounded-2xl border border-apn-line">
                    {item.children.map((child) => (
                      <Link key={child.to} to={child.to} className={`block px-2 py-3 text-[15px] font-medium rounded-lg ${focusRing} ${currentPath === child.to ? 'text-apn-orange' : 'text-apn-text'}`}>
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link key={item.label} to={item.to} className={`block py-3 font-display text-lg font-semibold tracking-tight rounded-md ${focusRing} ${isActive(item, currentPath) ? 'text-apn-orange' : 'text-apn-ink'}`}>
                {item.label}
              </Link>
            )
          )}
          <div className="grid grid-cols-1 gap-2.5 pt-3 pb-2">
            <a
              href={whatsappHref(currentPath)}
              target="_blank"
              rel="noopener noreferrer"
              data-cta="mobile-menu-whatsapp"
              className={`flex items-center justify-center gap-2 bg-[#25D366] text-[#0B2E17] py-3 rounded-full font-semibold ${focusRing}`}
            >
              <WhatsAppIcon />
              Chat on WhatsApp
            </a>
            <Link
              to="/book-call"
              data-cta="mobile-menu-book-call"
              className={`block text-center bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-3 rounded-full font-semibold ${focusRing}`}
            >
              Book a Free Strategy Call
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
